// Shared 52-card deck utilities for Classic Card Games.
// Card encoding: card = rank + suit * 13
//   rank: 0=A, 1=2, 2=3, ..., 9=T, 10=J, 11=Q, 12=K
//   suit: 0=♠, 1=♥, 2=♦, 3=♣

export const RANKS = 13;
export const SUITS = 4;
export const DECK_SIZE = 52;

export function cardRank(card: number): number { return card % RANKS; }
export function cardSuit(card: number): number { return Math.floor(card / RANKS); }
export function makeCard(rank: number, suit: number): number { return rank + suit * RANKS; }

const RANK_NAMES = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
const SUIT_SYMBOLS = ['♠', '♥', '♦', '♣'];

export function cardName(card: number): string {
  return RANK_NAMES[cardRank(card)] + SUIT_SYMBOLS[cardSuit(card)];
}

export function rankName(rank: number): string {
  return RANK_NAMES[rank] ?? String(rank);
}

// Seeded RNG (mulberry32)
export function rngStep(state: number): [number, number] {
  state = (state + 0x6D2B79F5) | 0;
  let t = Math.imul(state ^ (state >>> 15), 1 | state);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return [state, ((t ^ (t >>> 14)) >>> 0) / 4294967296];
}

export function shuffleDeck(rng: number): [number[], number] {
  const deck: number[] = Array.from({ length: DECK_SIZE }, (_, i) => i);
  for (let i = DECK_SIZE - 1; i > 0; i--) {
    let r: number;
    [rng, r] = rngStep(rng);
    const j = Math.floor(r * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  return [deck, rng];
}

// --- Hand evaluation for Texas Hold'em ---
// For evaluation: Ace = 12 (high), 2=0, 3=1, ..., K=11

function evalRank(rank: number): number {
  return rank === 0 ? 12 : rank - 1;
}

const EVAL_RANK_NAMES = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];

export function evalRankName(er: number): string {
  return EVAL_RANK_NAMES[er] ?? String(er);
}

export type HandScore = [number, number, number, number, number, number];

export interface HandEval {
  score: HandScore;
  description: string;
}

export function evaluateBestHand(cards: number[]): HandEval {
  let best: HandEval = { score: [-1, 0, 0, 0, 0, 0], description: '' };
  const n = cards.length;
  for (let a = 0; a < n - 4; a++) {
    for (let b = a + 1; b < n - 3; b++) {
      for (let c = b + 1; c < n - 2; c++) {
        for (let d = c + 1; d < n - 1; d++) {
          for (let e = d + 1; e < n; e++) {
            const result = evaluate5([cards[a], cards[b], cards[c], cards[d], cards[e]]);
            if (compareHandScores(result.score, best.score) > 0) best = result;
          }
        }
      }
    }
  }
  return best;
}

export function compareHandScores(a: HandScore, b: HandScore): number {
  for (let i = 0; i < 6; i++) {
    if (a[i] !== b[i]) return a[i] - b[i];
  }
  return 0;
}

function evaluate5(hand: number[]): HandEval {
  const ranks = hand.map(c => evalRank(cardRank(c)));
  const suits = hand.map(c => cardSuit(c));
  const sorted = [...ranks].sort((a, b) => b - a);
  const isFlush = suits.every(s => s === suits[0]);
  const straightHigh = checkStraight(sorted);

  if (isFlush && straightHigh !== null) {
    const royal = straightHigh === 12 && sorted[1] === 11;
    return {
      score: [8, straightHigh, 0, 0, 0, 0],
      description: royal ? 'Royal Flush' : `Straight Flush, ${evalRankName(straightHigh)} high`,
    };
  }

  const groups = groupByCount(sorted);

  if (groups[4]) {
    const [q] = groups[4]; const k = groups[1]?.[0] ?? 0;
    return { score: [7, q, k, 0, 0, 0], description: `Four ${evalRankName(q)}s` };
  }
  if (groups[3] && groups[2]) {
    const [t] = groups[3]; const [p] = groups[2];
    return { score: [6, t, p, 0, 0, 0], description: `Full House, ${evalRankName(t)}s full of ${evalRankName(p)}s` };
  }
  if (isFlush) {
    return { score: [5, sorted[0], sorted[1], sorted[2], sorted[3], sorted[4]], description: `Flush, ${evalRankName(sorted[0])} high` };
  }
  if (straightHigh !== null) {
    return { score: [4, straightHigh, 0, 0, 0, 0], description: `Straight, ${evalRankName(straightHigh)} high` };
  }
  if (groups[3]) {
    const [t] = groups[3]; const ks = groups[1] ?? [];
    return { score: [3, t, ks[0] ?? 0, ks[1] ?? 0, 0, 0], description: `Three ${evalRankName(t)}s` };
  }
  if (groups[2] && groups[2].length >= 2) {
    const [h, l] = groups[2]; const k = groups[1]?.[0] ?? 0;
    return { score: [2, h, l, k, 0, 0], description: `Two Pair, ${evalRankName(h)}s and ${evalRankName(l)}s` };
  }
  if (groups[2]) {
    const [p] = groups[2]; const ks = sorted.filter(r => r !== p);
    return { score: [1, p, ks[0] ?? 0, ks[1] ?? 0, ks[2] ?? 0, 0], description: `Pair of ${evalRankName(p)}s` };
  }
  return { score: [0, sorted[0], sorted[1], sorted[2], sorted[3], sorted[4]], description: `${evalRankName(sorted[0])} high` };
}

function checkStraight(sorted: number[]): number | null {
  if (sorted[0] - sorted[4] === 4 && new Set(sorted).size === 5) return sorted[0];
  // Wheel: A-2-3-4-5 → evalRanks [12, 3, 2, 1, 0]
  if (sorted[0] === 12 && sorted[1] === 3 && sorted[2] === 2 && sorted[3] === 1 && sorted[4] === 0) return 3;
  return null;
}

function groupByCount(sorted: number[]): Record<number, number[]> {
  const counts: Record<number, number> = {};
  for (const r of sorted) counts[r] = (counts[r] ?? 0) + 1;
  const groups: Record<number, number[]> = {};
  for (const [r, cnt] of Object.entries(counts)) {
    if (!groups[cnt]) groups[cnt] = [];
    groups[cnt].push(Number(r));
  }
  for (const key of Object.keys(groups)) groups[Number(key)].sort((a, b) => b - a);
  return groups;
}

// --- Gin Rummy utilities ---

// Deadwood value: A=1, 2-10=face, J/Q/K=10
export function deadwoodValue(card: number): number {
  const rank = cardRank(card);
  if (rank === 0) return 1;
  if (rank >= 10) return 10;
  return rank + 1;
}

// Find all valid melds (sets and runs) in a hand
export function findAllMelds(hand: number[]): number[][] {
  const melds: number[][] = [];

  // Sets: 3-4 cards of same rank, different suits
  const byRank: Record<number, number[]> = {};
  for (const c of hand) {
    const r = cardRank(c);
    if (!byRank[r]) byRank[r] = [];
    byRank[r].push(c);
  }
  for (const cards of Object.values(byRank)) {
    if (cards.length < 3) continue;
    for (let i = 0; i < cards.length - 2; i++)
      for (let j = i + 1; j < cards.length - 1; j++)
        for (let k = j + 1; k < cards.length; k++)
          melds.push([cards[i], cards[j], cards[k]]);
    if (cards.length === 4) melds.push([...cards]);
  }

  // Runs: 3+ consecutive ranks of the same suit (Ace always low: A-2-3 valid, Q-K-A not)
  const bySuit: Record<number, number[]> = {};
  for (const c of hand) {
    const s = cardSuit(c);
    if (!bySuit[s]) bySuit[s] = [];
    bySuit[s].push(c);
  }
  for (const cards of Object.values(bySuit)) {
    if (cards.length < 3) continue;
    const sorted = [...cards].sort((a, b) => cardRank(a) - cardRank(b));
    for (let start = 0; start < sorted.length; start++) {
      for (let end = start + 2; end < sorted.length; end++) {
        const slice = sorted.slice(start, end + 1);
        const consecutive = slice.every((c, i) => i === 0 || cardRank(c) === cardRank(slice[i - 1]) + 1);
        if (consecutive) melds.push(slice);
      }
    }
  }

  return melds;
}

// Find the partition of hand into melds that minimizes deadwood
export function findBestMelds(hand: number[]): { melds: number[][]; deadwood: number; unmelded: number[] } {
  const allMelds = findAllMelds(hand);
  let bestDeadwood = hand.reduce((s, c) => s + deadwoodValue(c), 0);
  let bestMelds: number[][] = [];
  let bestUnmelded = [...hand];

  function bt(idx: number, used: Set<number>, chosen: number[][]): void {
    const unmelded = hand.filter(c => !used.has(c));
    const dw = unmelded.reduce((s, c) => s + deadwoodValue(c), 0);
    if (dw < bestDeadwood) {
      bestDeadwood = dw;
      bestMelds = chosen.map(m => [...m]);
      bestUnmelded = unmelded;
    }
    if (dw === 0) return;
    for (let i = idx; i < allMelds.length; i++) {
      const meld = allMelds[i];
      if (meld.some(c => used.has(c))) continue;
      for (const c of meld) used.add(c);
      chosen.push(meld);
      bt(i + 1, used, chosen);
      chosen.pop();
      for (const c of meld) used.delete(c);
    }
  }

  bt(0, new Set(), []);
  return { melds: bestMelds, deadwood: bestDeadwood, unmelded: bestUnmelded };
}

// Can a card be laid off on a meld?
export function canLayOff(card: number, meld: number[]): boolean {
  if (meld.length === 0) return false;
  const isSet = meld.every(c => cardRank(c) === cardRank(meld[0]));
  if (isSet) {
    return cardRank(card) === cardRank(meld[0]) && !meld.includes(card);
  }
  // Run: same suit, extend at either end
  if (cardSuit(card) !== cardSuit(meld[0])) return false;
  const meldRanks = meld.map(cardRank).sort((a, b) => a - b);
  const r = cardRank(card);
  return r === meldRanks[0] - 1 || r === meldRanks[meldRanks.length - 1] + 1;
}
